import React, { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { apiCall } from "../lib/api-client.js";
import {
  platformSkillHubArchivePath,
  platformSkillHubItemsPath,
  platformSkillHubItemPath,
  platformSkillHubPublishPath,
  platformSkillHubRollbackPath,
  platformSkillHubUnpublishPath,
  platformSkillHubValidatePath,
} from "../lib/api-paths.js";
import {
  ApiError,
  type SkillHubAdminItemView,
  type SkillHubItemKind,
  type SkillHubItemStatus,
  type SkillHubRevisionValidationStatus,
  type SkillHubSuccessEnvelope,
} from "../lib/api-types.js";
import { authAtom } from "../lib/auth-store.js";
import { formatDateTime } from "../lib/format.js";
import {
  buildSkillHubMetadata,
  canLifecycle,
  draftValidationStatus,
  fileToBase64Chunked,
  filterSkillHubItems,
  formatBytes,
  itemVersionLabel,
  type SkillHubLifecycleAction,
  type SkillHubMetadataFormInput,
  unwrapSkillHubData,
} from "../lib/skill-hub.js";
import { useApiQuery } from "../lib/useApiQuery.js";
import { Drawer } from "../components/Drawer.js";
import { Empty } from "../components/Empty.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
} from "../components/FormField.js";
import { Modal } from "../components/Modal.js";
import { PageHeader } from "../components/PageHeader.js";
import { Table, type ColumnDef } from "../components/Table.js";

/**
 * Skill Hub 平台 Catalog 最简管理页（PR-3）。
 *
 * 超管独立完成：创建 → 上传 ZIP → 校验 → 发布 / 撤下 / 回滚 + 审计查看。
 * 不做第二审批人、不做在线文件编辑器、不做个人分享、不做运营 dashboard。
 * API 经 admin-portal `/api/platform/skill-hub/*` 透传 dosia-enterprise service face。
 */

const EMPTY_META_FORM: SkillHubMetadataFormInput = {
  name: "",
  description: "",
  category: "",
  tags: "",
  longDescription: "",
  useCases: "",
  minDosiaVersion: "",
  providedByPlugin: "",
  featuredRank: "",
};

const EMPTY_CREATE = {
  slug: "",
  kind: "skill" as SkillHubItemKind,
  ...EMPTY_META_FORM,
};

const STATUS_LABEL: Record<SkillHubItemStatus, string> = {
  published: "已发布",
  draft: "草稿",
  unpublished: "已撤下",
};

const STATUS_CLASS: Record<SkillHubItemStatus, string> = {
  published: "bg-emerald-400/15 text-emerald-700 border-emerald-400/40",
  draft: "bg-dosia-bg-lighter text-dosia-text-secondary border-dosia-border",
  unpublished: "bg-amber-500/15 text-amber-700 border-amber-500/40",
};

const VALIDATION_LABEL: Record<SkillHubRevisionValidationStatus, string> = {
  uploaded: "已上传",
  validated: "已校验",
  invalid: "校验失败",
};

const VALIDATION_CLASS: Record<SkillHubRevisionValidationStatus, string> = {
  uploaded: "bg-dosia-bg-lighter text-dosia-text-secondary border-dosia-border",
  validated: "bg-emerald-400/15 text-emerald-700 border-emerald-400/40",
  invalid: "bg-amber-500/15 text-amber-700 border-amber-500/40",
};

const KIND_LABEL: Record<SkillHubItemKind, string> = {
  skill: "Skill",
  plugin: "Plugin",
};

const CONFIRM_COPY: Record<
  Exclude<SkillHubLifecycleAction, "validate">,
  { title: string; body: string; confirmLabel: string }
> = {
  publish: {
    title: "确认发布",
    body: "发布后员工端 Skill Hub 目录将可见该版本。确认继续？",
    confirmLabel: "确认发布",
  },
  unpublish: {
    title: "确认撤下",
    body: "撤下后公开目录将不再展示该条目（包与历史仍保留，可再发布）。确认继续？",
    confirmLabel: "确认撤下",
  },
  rollback: {
    title: "确认回滚",
    body: "将把公开当前版本回退到 previousRevision，并清空草稿指针。确认继续？",
    confirmLabel: "确认回滚",
  },
};

function Pill({
  label,
  className,
}: {
  label: string;
  className: string;
}): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

function metaFormFromItem(item: SkillHubAdminItemView): SkillHubMetadataFormInput {
  const m = item.metadata;
  return {
    name: m.name,
    description: m.description,
    category: m.category,
    tags: (m.tags ?? []).join(", "),
    longDescription: m.longDescription ?? "",
    useCases: (m.useCases ?? []).join(", "),
    minDosiaVersion: m.minDosiaVersion ?? "",
    providedByPlugin: m.providedByPlugin ?? "",
    featuredRank: m.featuredRank !== undefined ? String(m.featuredRank) : "",
  };
}

function SnapshotBlock({
  label,
  value,
}: {
  label: string;
  value: unknown;
}): JSX.Element {
  const text =
    value === null || value === undefined
      ? "null"
      : typeof value === "string"
        ? value
        : JSON.stringify(value, null, 2);
  return (
    <details className="rounded border border-dosia-border bg-dosia-bg px-2 py-1">
      <summary className="cursor-pointer text-xs text-dosia-text-secondary select-none">
        {label}
      </summary>
      <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all text-[11px] font-mono text-dosia-text">
        {text}
      </pre>
    </details>
  );
}

export function SkillHubAdmin(): JSX.Element {
  const token = useAtomValue(authAtom)?.token;
  const listQ = useApiQuery<SkillHubSuccessEnvelope<SkillHubAdminItemView[]>>(
    platformSkillHubItemsPath(),
    [],
  );

  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<"" | SkillHubItemKind>("");
  const [statusFilter, setStatusFilter] = useState<"" | SkillHubItemStatus>("");

  const [actionError, setActionError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SkillHubAdminItemView | null>(null);
  const [editForm, setEditForm] = useState<SkillHubMetadataFormInput>(EMPTY_META_FORM);
  const [savingMeta, setSavingMeta] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [uploadName, setUploadName] = useState<string | null>(null);
  const [uploadSize, setUploadSize] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const [busyAction, setBusyAction] = useState<SkillHubLifecycleAction | null>(null);
  const [confirmAction, setConfirmAction] = useState<Exclude<
    SkillHubLifecycleAction,
    "validate"
  > | null>(null);

  const items = useMemo(() => {
    try {
      if (!listQ.data) return [] as SkillHubAdminItemView[];
      const data = unwrapSkillHubData<unknown>(listQ.data);
      return Array.isArray(data) ? (data as SkillHubAdminItemView[]) : [];
    } catch {
      return [] as SkillHubAdminItemView[];
    }
  }, [listQ.data]);

  const filtered = useMemo(
    () => filterSkillHubItems(items, { search, kind: kindFilter, status: statusFilter }),
    [items, search, kindFilter, statusFilter],
  );

  // 列表刷新后，若详情仍打开，用服务端最新行替换本地 selected。
  useEffect(() => {
    if (!selected) return;
    const next = items.find((i) => i.itemId === selected.itemId);
    if (next && next !== selected) {
      setSelected(next);
      setEditForm(metaFormFromItem(next));
    }
  }, [items, selected]);

  const applyItem = (item: SkillHubAdminItemView): void => {
    setSelected(item);
    setEditForm(metaFormFromItem(item));
    setActionError(null);
    setUploadName(null);
    setUploadSize(null);
  };

  const mutateItem = async (
    method: string,
    path: string,
    body?: unknown,
  ): Promise<SkillHubAdminItemView> => {
    if (!token) throw new ApiError(401, "unauthorized", "未登录");
    const raw = await apiCall<unknown>(method, path, { token, body });
    return unwrapSkillHubData<SkillHubAdminItemView>(raw);
  };

  const afterMutation = async (item: SkillHubAdminItemView): Promise<void> => {
    applyItem(item);
    await listQ.refetch();
  };

  const openCreate = (): void => {
    setCreateForm(EMPTY_CREATE);
    setCreateError(null);
    setCreateOpen(true);
  };

  const submitCreate = async (): Promise<void> => {
    if (!token) return;
    if (!createForm.slug.trim() || !createForm.name.trim() || !createForm.description.trim() || !createForm.category.trim()) {
      setCreateError("slug / name / description / category 为必填");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const metadata = buildSkillHubMetadata(createForm);
      const item = await mutateItem("POST", platformSkillHubItemsPath(), {
        slug: createForm.slug.trim(),
        kind: createForm.kind,
        metadata,
      });
      setCreateOpen(false);
      await afterMutation(item);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "创建失败");
    } finally {
      setCreating(false);
    }
  };

  const saveMetadata = async (): Promise<void> => {
    if (!selected || !token) return;
    if (!editForm.name.trim() || !editForm.description.trim() || !editForm.category.trim()) {
      setActionError("name / description / category 为必填");
      return;
    }
    setSavingMeta(true);
    setActionError(null);
    try {
      const metadata = buildSkillHubMetadata(editForm);
      const item = await mutateItem("PATCH", platformSkillHubItemPath(selected.itemId), {
        metadata,
      });
      await afterMutation(item);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSavingMeta(false);
    }
  };

  const onPickZip = async (file: File | null): Promise<void> => {
    if (!file || !selected || !token) return;
    if (!file.name.toLowerCase().endsWith(".zip") && file.type !== "application/zip") {
      setActionError("请选择 .zip 文件");
      return;
    }
    setUploading(true);
    setActionError(null);
    setUploadName(file.name);
    setUploadSize(file.size);
    try {
      const contentBase64 = await fileToBase64Chunked(file);
      const item = await mutateItem(
        "POST",
        platformSkillHubArchivePath(selected.itemId),
        { contentBase64 },
      );
      await afterMutation(item);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "上传失败");
    } finally {
      setUploading(false);
    }
  };

  const runValidate = async (): Promise<void> => {
    if (!selected || !token) return;
    setBusyAction("validate");
    setActionError(null);
    try {
      const item = await mutateItem(
        "POST",
        platformSkillHubValidatePath(selected.itemId),
        {},
      );
      await afterMutation(item);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "校验失败");
    } finally {
      setBusyAction(null);
    }
  };

  const runConfirmed = async (): Promise<void> => {
    if (!selected || !token || !confirmAction) return;
    const action = confirmAction;
    setBusyAction(action);
    setActionError(null);
    try {
      const path =
        action === "publish"
          ? platformSkillHubPublishPath(selected.itemId)
          : action === "unpublish"
            ? platformSkillHubUnpublishPath(selected.itemId)
            : platformSkillHubRollbackPath(selected.itemId);
      const item = await mutateItem("POST", path, { confirm: true });
      setConfirmAction(null);
      await afterMutation(item);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "操作失败");
    } finally {
      setBusyAction(null);
    }
  };

  const columns: ColumnDef<SkillHubAdminItemView>[] = [
    {
      header: "名称 / slug",
      render: (row) => (
        <div className="min-w-0">
          <div className="font-medium text-dosia-text truncate">{row.metadata.name}</div>
          <div className="text-xs text-dosia-text-dim font-mono truncate">{row.slug}</div>
        </div>
      ),
    },
    {
      header: "类型",
      width: "w-20",
      render: (row) => KIND_LABEL[row.kind],
    },
    {
      header: "状态",
      width: "w-24",
      render: (row) => (
        <Pill label={STATUS_LABEL[row.status]} className={STATUS_CLASS[row.status]} />
      ),
    },
    {
      header: "当前版本",
      width: "w-28",
      render: (row) => (
        <span className="font-mono text-xs">{itemVersionLabel(row)}</span>
      ),
    },
    {
      header: "草稿校验",
      width: "w-28",
      render: (row) => {
        const st = draftValidationStatus(row);
        if (!st) return <span className="text-dosia-text-dim">—</span>;
        return <Pill label={VALIDATION_LABEL[st]} className={VALIDATION_CLASS[st]} />;
      },
    },
    {
      header: "更新时间",
      width: "w-40",
      render: (row) => (
        <span className="text-xs text-dosia-text-secondary">{formatDateTime(row.updatedAt)}</span>
      ),
    },
  ];

  const draftStatus = selected ? draftValidationStatus(selected) : null;

  return (
    <div className="space-y-4" data-testid="skill-hub-admin">
      <PageHeader
        title="Skill Hub"
        subtitle="平台技能 / 插件目录：创建、上传完整 ZIP、校验、发布、撤下与回滚。超管独立完成，无第二审批人。"
        actions={
          <>
            <SecondaryButton type="button" onClick={() => void listQ.refetch()} disabled={listQ.loading}>
              刷新
            </SecondaryButton>
            <PrimaryButton type="button" onClick={openCreate}>
              新建条目
            </PrimaryButton>
          </>
        }
      />

      {listQ.error ? (
        <ErrorBanner onDismiss={() => void listQ.refetch()}>
          加载列表失败：{listQ.error.message}
        </ErrorBanner>
      ) : null}
      {actionError ? (
        <ErrorBanner onDismiss={() => setActionError(null)}>{actionError}</ErrorBanner>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <FormField label="搜索" className="min-w-[200px] flex-1">
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="名称 / slug / 描述"
            aria-label="搜索 Skill Hub 条目"
          />
        </FormField>
        <FormField label="类型" className="w-36">
          <Select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as "" | SkillHubItemKind)}
            aria-label="筛选类型"
          >
            <option value="">全部</option>
            <option value="skill">Skill</option>
            <option value="plugin">Plugin</option>
          </Select>
        </FormField>
        <FormField label="状态" className="w-36">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as "" | SkillHubItemStatus)}
            aria-label="筛选状态"
          >
            <option value="">全部</option>
            <option value="draft">草稿</option>
            <option value="published">已发布</option>
            <option value="unpublished">已撤下</option>
          </Select>
        </FormField>
      </div>

      <Table
        columns={columns}
        rows={filtered}
        rowKey={(row) => row.itemId}
        loading={listQ.loading && items.length === 0}
        onRowClick={(row) => applyItem(row)}
        empty={
          <Empty
            title={items.length === 0 ? "暂无 Skill Hub 条目" : "没有匹配的条目"}
            description={
              items.length === 0
                ? "点击「新建条目」创建第一个 skill 或 plugin。"
                : "试试调整搜索或筛选条件。"
            }
            action={
              items.length === 0 ? (
                <PrimaryButton type="button" onClick={openCreate}>
                  新建条目
                </PrimaryButton>
              ) : undefined
            }
          />
        }
      />

      {/* 新建 */}
      <Modal
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        title="新建 Skill Hub 条目"
        size="lg"
        footer={
          <>
            <SecondaryButton type="button" onClick={() => setCreateOpen(false)} disabled={creating}>
              取消
            </SecondaryButton>
            <PrimaryButton type="button" onClick={() => void submitCreate()} disabled={creating}>
              {creating ? "创建中…" : "创建"}
            </PrimaryButton>
          </>
        }
      >
        <div className="space-y-3">
          {createError ? <ErrorBanner>{createError}</ErrorBanner> : null}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="slug" required>
              <TextInput
                value={createForm.slug}
                onChange={(e) => setCreateForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="my-skill"
                aria-label="slug"
              />
            </FormField>
            <FormField label="类型" required>
              <Select
                value={createForm.kind}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, kind: e.target.value as SkillHubItemKind }))
                }
                aria-label="kind"
              >
                <option value="skill">skill</option>
                <option value="plugin">plugin</option>
              </Select>
            </FormField>
          </div>
          <MetadataFields
            form={createForm}
            onChange={(patch) => setCreateForm((f) => ({ ...f, ...patch }))}
          />
        </div>
      </Modal>

      {/* 详情 */}
      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? selected.metadata.name : "详情"}
        width={560}
      >
        {selected ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Pill
                label={STATUS_LABEL[selected.status]}
                className={STATUS_CLASS[selected.status]}
              />
              <span className="text-dosia-text-secondary">{KIND_LABEL[selected.kind]}</span>
              <span className="font-mono text-xs text-dosia-text-dim">{selected.itemId}</span>
            </div>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-dosia-text">基本信息</h3>
              <FormField label="slug（只读）">
                <TextInput value={selected.slug} readOnly disabled aria-label="slug 只读" />
              </FormField>
              <FormField label="kind（只读）">
                <TextInput value={selected.kind} readOnly disabled aria-label="kind 只读" />
              </FormField>
              <MetadataFields
                form={editForm}
                onChange={(patch) => setEditForm((f) => ({ ...f, ...patch }))}
              />
              <PrimaryButton
                type="button"
                onClick={() => void saveMetadata()}
                disabled={savingMeta}
              >
                {savingMeta ? "保存中…" : "保存 metadata"}
              </PrimaryButton>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-dosia-text">上传 ZIP 包</h3>
              <p className="text-xs text-dosia-text-secondary">
                上传完整 skill/plugin 归档；浏览器不解析 ZIP，仅 base64 提交。校验由服务端完成。
              </p>
              <input
                type="file"
                accept=".zip,application/zip"
                aria-label="上传 ZIP"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  void onPickZip(f);
                  e.target.value = "";
                }}
                className="block w-full text-sm text-dosia-text-secondary file:mr-3 file:rounded-md file:border file:border-dosia-border file:bg-dosia-bg-lighter file:px-3 file:py-1.5 file:text-sm file:text-dosia-text"
              />
              {uploadName ? (
                <p className="text-xs text-dosia-text-secondary">
                  {uploading ? "上传中…" : "已选"}：{uploadName}
                  {uploadSize != null ? ` · ${formatBytes(uploadSize)}` : ""}
                </p>
              ) : null}
              {selected.draftRevision ? (
                <p className="text-xs text-dosia-text-dim font-mono">
                  draft：{selected.draftRevision}
                  {draftStatus ? ` · ${VALIDATION_LABEL[draftStatus]}` : ""}
                </p>
              ) : null}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-dosia-text">生命周期</h3>
              <div className="flex flex-wrap gap-2">
                <SecondaryButton
                  type="button"
                  disabled={!canLifecycle(selected, "validate") || busyAction !== null}
                  onClick={() => void runValidate()}
                >
                  {busyAction === "validate" ? "校验中…" : "校验草稿"}
                </SecondaryButton>
                <PrimaryButton
                  type="button"
                  disabled={!canLifecycle(selected, "publish") || busyAction !== null}
                  onClick={() => setConfirmAction("publish")}
                >
                  发布
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  disabled={!canLifecycle(selected, "unpublish") || busyAction !== null}
                  onClick={() => setConfirmAction("unpublish")}
                >
                  撤下
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  disabled={!canLifecycle(selected, "rollback") || busyAction !== null}
                  onClick={() => setConfirmAction("rollback")}
                >
                  回滚
                </SecondaryButton>
              </div>
              {draftStatus === "invalid" ? (
                <p className="text-xs text-amber-700">
                  草稿校验失败
                  {selected.revisions.find((r) => r.revision === selected.draftRevision)
                    ?.validationError
                    ? `：${
                        selected.revisions.find((r) => r.revision === selected.draftRevision)
                          ?.validationError
                      }`
                    : ""}
                </p>
              ) : null}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-dosia-text">Revisions</h3>
              {selected.revisions.length === 0 ? (
                <p className="text-xs text-dosia-text-dim">尚无 revision（先上传 ZIP）</p>
              ) : (
                <div className="border border-dosia-border rounded-md overflow-hidden">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-dosia-bg-lighter text-dosia-text-secondary">
                      <tr>
                        <th className="px-2 py-1.5 font-medium">version</th>
                        <th className="px-2 py-1.5 font-medium">revision</th>
                        <th className="px-2 py-1.5 font-medium">校验</th>
                        <th className="px-2 py-1.5 font-medium">actor</th>
                        <th className="px-2 py-1.5 font-medium">时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.revisions.map((r) => (
                        <tr key={r.revision} className="border-t border-dosia-border-light">
                          <td className="px-2 py-1.5 font-mono">{r.version}</td>
                          <td className="px-2 py-1.5 font-mono truncate max-w-[8rem]" title={r.revision}>
                            {r.revision.slice(0, 16)}
                          </td>
                          <td className="px-2 py-1.5">
                            <Pill
                              label={VALIDATION_LABEL[r.validationStatus]}
                              className={VALIDATION_CLASS[r.validationStatus]}
                            />
                            {r.validationError ? (
                              <div className="mt-0.5 text-amber-700 max-w-[10rem] truncate" title={r.validationError}>
                                {r.validationError}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-2 py-1.5 text-dosia-text-secondary truncate max-w-[6rem]">
                            {r.createdBy}
                          </td>
                          <td className="px-2 py-1.5 text-dosia-text-secondary whitespace-nowrap">
                            {formatDateTime(r.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-dosia-text">最近审计</h3>
              {selected.recentAudit.length === 0 ? (
                <p className="text-xs text-dosia-text-dim">暂无审计事件</p>
              ) : (
                <ul className="space-y-2">
                  {selected.recentAudit.map((ev) => (
                    <li
                      key={ev.eventId}
                      className="rounded-md border border-dosia-border bg-dosia-bg-light px-3 py-2 space-y-1.5"
                      data-testid="skill-hub-audit-event"
                    >
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
                        <span className="font-medium text-dosia-text">{ev.action}</span>
                        <span className="text-dosia-text-secondary">{ev.actor}</span>
                        <span className="text-dosia-text-dim">{formatDateTime(ev.createdAt)}</span>
                      </div>
                      {ev.requestId ? (
                        <div className="text-[11px] font-mono text-dosia-text-dim">
                          requestId: {ev.requestId}
                        </div>
                      ) : null}
                      <div className="grid gap-1">
                        <SnapshotBlock label="old snapshot" value={ev.oldSnapshot} />
                        <SnapshotBlock label="new snapshot" value={ev.newSnapshot} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </Drawer>

      {/* 发布 / 撤下 / 回滚 显式确认 */}
      <Modal
        open={confirmAction !== null}
        onClose={() => busyAction === null && setConfirmAction(null)}
        title={confirmAction ? CONFIRM_COPY[confirmAction].title : ""}
        size="sm"
        footer={
          <>
            <SecondaryButton
              type="button"
              onClick={() => setConfirmAction(null)}
              disabled={busyAction !== null}
            >
              取消
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={() => void runConfirmed()}
              disabled={busyAction !== null}
              data-testid="skill-hub-confirm-action"
            >
              {busyAction && confirmAction
                ? "处理中…"
                : confirmAction
                  ? CONFIRM_COPY[confirmAction].confirmLabel
                  : "确认"}
            </PrimaryButton>
          </>
        }
      >
        {confirmAction ? (
          <p className="text-sm text-dosia-text-secondary">{CONFIRM_COPY[confirmAction].body}</p>
        ) : null}
      </Modal>
    </div>
  );
}

function MetadataFields({
  form,
  onChange,
}: {
  form: SkillHubMetadataFormInput;
  onChange: (patch: Partial<SkillHubMetadataFormInput>) => void;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <FormField label="name" required>
        <TextInput
          value={form.name}
          onChange={(e) => onChange({ name: e.target.value })}
          aria-label="name"
        />
      </FormField>
      <FormField label="description" required>
        <TextArea
          value={form.description}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
          aria-label="description"
        />
      </FormField>
      <FormField label="category" required>
        <TextInput
          value={form.category}
          onChange={(e) => onChange({ category: e.target.value })}
          aria-label="category"
        />
      </FormField>
      <FormField label="tags" hint="逗号分隔" required>
        <TextInput
          value={form.tags}
          onChange={(e) => onChange({ tags: e.target.value })}
          placeholder="ops, report"
          aria-label="tags"
        />
      </FormField>
      <FormField label="longDescription（可选）">
        <TextArea
          value={form.longDescription ?? ""}
          onChange={(e) => onChange({ longDescription: e.target.value })}
          rows={3}
          aria-label="longDescription"
        />
      </FormField>
      <FormField label="useCases（可选）" hint="逗号分隔">
        <TextInput
          value={form.useCases ?? ""}
          onChange={(e) => onChange({ useCases: e.target.value })}
          aria-label="useCases"
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="minDosiaVersion（可选）">
          <TextInput
            value={form.minDosiaVersion ?? ""}
            onChange={(e) => onChange({ minDosiaVersion: e.target.value })}
            placeholder="0.3.0"
            aria-label="minDosiaVersion"
          />
        </FormField>
        <FormField label="featuredRank（可选）">
          <TextInput
            value={form.featuredRank ?? ""}
            onChange={(e) => onChange({ featuredRank: e.target.value })}
            placeholder="0"
            aria-label="featuredRank"
          />
        </FormField>
      </div>
      <FormField label="providedByPlugin（可选）">
        <TextInput
          value={form.providedByPlugin ?? ""}
          onChange={(e) => onChange({ providedByPlugin: e.target.value })}
          aria-label="providedByPlugin"
        />
      </FormField>
    </div>
  );
}
